import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { Tooltip } from '@elfsight/widget-modal';
import { useTranslation } from 'react-i18next';
import type { PostSourceDisplayType } from '../../../types';
import { Badge, Link } from '../../lib/components';
import { useSocialFeedSettings } from '../../utils';
import { usePost } from '../Post';
import { ShadowIcon } from './ShadowIcon';

export const SOURCE_ICON_SIZE = 24;

interface SourceLinkProps {
  url: string;
  show: boolean;
  display: PostSourceDisplayType;
  title: string;
  icon: string;
  sourceAlign?: CSSProperties['alignSelf'];
  color?: string;
  shadow?: boolean;
}

export function SourceLink({
  url,
  show,
  display,
  title,
  color,
  icon,
  sourceAlign = 'center',
  ...forwardingProps
}: SourceLinkProps) {
  const {
    tooltipBackgroundColor,
    tooltipTextColor,
    googleAnalytics = [],
    widgetId = '',
    editMode,
    font
  } = useSocialFeedSettings();
  const { t } = useTranslation();
  const { id, source } = usePost();
  const postUrl = source.url || '';

  const handleClick = () => {
    sendGoogleAnalyticsEvents({
      eventType: 'socialPlatformOpened',
      googleAnalyticsEvents: googleAnalytics,
      editMode,
      valueMapping: {
        widgetId,
        postId: id,
        postUrl,
        entryPoint: 'post'
      }
    });
  };

  if (!show || !display) {
    return null;
  }

  const translatedLabel = `${t('Posted on')} ${title}`;

  if (display === 'badge') {
    return (
      <StyledLink
        {...forwardingProps}
        href={url}
        aria-label={translatedLabel}
        hoverEffect="none"
        _sourceAlign={sourceAlign}
        onClick={handleClick}
      >
        <Badge color={color}>{title}</Badge>
      </StyledLink>
    );
  }

  return (
    <Tooltip
      title={translatedLabel}
      background={tooltipBackgroundColor}
      fontColor={tooltipTextColor}
      font={font}
    >
      <StyledLink
        {...forwardingProps}
        href={url}
        aria-label={translatedLabel}
        onClick={handleClick}
      >
        <ShadowIcon
          src={icon}
          alt={title}
          fill={color}
          size={SOURCE_ICON_SIZE}
        />
      </StyledLink>
    </Tooltip>
  );
}

const StyledLink = styled(Link)<{
  _sourceAlign?: CSSProperties['alignSelf'];
}>`
  align-self: ${({ _sourceAlign }) => _sourceAlign};
`;
